import * as React from 'react'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { IHitDetailsModal } from './Types'
import HitModalStyle from './Style'
import { formatLongNumber } from 'utils/number'
import { format } from 'date-fns'
import { DEFAULT_AVATAR_URL } from 'components/HitCard/Utils'
import Keyword from 'Keyword/Keyword'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Snackbar,
} from '@mui/material'
import RespondTextArea from 'components/RespondTextArea/RespondTextArea'
import axios from 'axios'
import { TikTok } from 'react-tiktok'
import { InstagramEmbed } from 'react-social-media-embed'
import {
  Platform,
  useGetResponseLazyQuery,
  useInteractedWithHitMutation,
} from 'graphql/generated/graphql'
import { useParams } from 'react-router-dom'
import ClassificationTag from 'components/HitCard/components/ClassificationTag'

interface ResponseType {
  comment1: string
  comment2: string
  comment3: string
}

export default function HitDetailsModal({
  openModal,
  closeModal,
  headerProps,
  body,
}: IHitDetailsModal): JSX.Element {
  const [value, setValue] = React.useState('')
  const [loading, setIsLoading] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState<null | string>(null)
  const displayDate = format(headerProps?.date, 'd MMM y')
  const [redditHtml, setRedditHtml] = React.useState('')
  const [addInteraction] = useInteractedWithHitMutation()
  const [responses, setResponses] = React.useState<ResponseType | undefined>()
  const params = useParams()
  const [getResponse, { loading: responseLoading, error, data }] =
    useGetResponseLazyQuery()
  const [open, setOpen] = React.useState(false)
  const handleClose = (): void => {
    setOpen(false)
  }

  React.useEffect(() => {
    if (data?.respond !== '{}' && data) {
      const parsed = JSON.parse(data.respond)
      setResponses(parsed)
    }
  }, [data])

  const getRedditEmbed = React.useCallback(async () => {
    if (headerProps.platform === 'Reddit') {
      const redditUrl = `https://www.reddit.com/oembed?url=https://reddit.com${headerProps.postURL}/`
      const result = await axios.get(`/reddit-proxy?url=${redditUrl}`)
      setRedditHtml(result.data?.html)
    }
  }, [])

  React.useEffect(() => {
    void getRedditEmbed()
  }, [])

  const highlightKeyword = (text: string): string => {
    if (headerProps.keyword) {
      if (headerProps.keyword.length === 1) {
        const re = new RegExp('(' + headerProps.keyword[0] + ')', 'ig')
        const newText = text.replace(re, `<mark>$1</mark>`)
        return newText
      } else {
        let newText = text
        headerProps.keyword.forEach((keyword) => {
          const re = new RegExp('(' + keyword + ')', 'ig')
          newText = newText.replace(re, `<mark>$1</mark>`)
        })
        return newText
      }
    }
    return text
  }
  const handleRespond = (): void => {
    if (headerProps.platform === 'Youtube') {
      return respondYoutube()
    } else if (headerProps.platform === 'Reddit') {
      return respondReddit()
    } else {
      console.log(headerProps.platform)
    }
  }

  const returnEmbed = (): JSX.Element => {
    if (headerProps.platform === 'Youtube') {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <iframe
            width={600}
            height={400}
            style={{ border: 'none' }}
            src={`https://www.youtube.com/embed/${headerProps.hitItemId}`}
          ></iframe>
        </div>
      )
    } else if (headerProps.platform === 'TikTok') {
      return <TikTok url={headerProps.postURL} />
    } else if (headerProps.platform === 'Instagram') {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <InstagramEmbed url={headerProps.postURL} width={500} />
        </div>
      )
    } else if (headerProps.platform === 'Reddit') {
      return <div dangerouslySetInnerHTML={{ __html: redditHtml }} />
    } else {
      return (
        <HitModalStyle.ThumbnailStyle
          src={headerProps?.imageSource}
          alt="Thumbnail hit"
        />
      )
    }
  }

  const respondReddit = (): void => {
    setIsLoading(true)
    const accessToken = localStorage.getItem('redditToken')
    const url = 'https://oauth.reddit.com/api/comment'
    const data = {
      thing_id: `t3_${headerProps.hitItemId}`,
      text: value,
      api_type: 'json',
    }
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `bearer ${accessToken ?? ''}`,
      'User-Agent': 'Nalopod v1 (by u/Dazzling_Price_5207)', // TODO Add current user here.
    }
    axios
      .post(url, data, {
        headers,
      })
      .then(async (res) => {
        if (!headerProps.interactedWith) {
          await addInteraction({
            variables: {
              campaignId: params.campaignId ?? '',
              hitId: headerProps.hitItemId,
            },
          })
        }

        setIsLoading(false)
        setValue('')
        setErrorMessage(null)
      })
      .catch((e) => {
        if (e.response.status === 401) {
          setErrorMessage(
            'Please connect your socials before replying to this hit. To connect your socials, go to "Connect Social" section in our app.'
          )
        } else {
          setErrorMessage(
            e.response.data.message ??
              'Something went wrong please try again later.'
          )
        }
        setIsLoading(false)
        console.error(e)
      })
  }
  const respondYoutube = (): void => {
    setIsLoading(true)
    const accessToken = localStorage.getItem('google_access_token')
    const url =
      'https://www.googleapis.com/youtube/v3/commentThreads?part=id,snippet'
    const data = {
      snippet: {
        videoId: headerProps.hitItemId,
        topLevelComment: {
          snippet: {
            textOriginal: value,
          },
        },
      },
    }
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken ?? ''}`,
    }
    axios
      .post(url, JSON.stringify(data), {
        headers,
      })
      .then(async (res) => {
        if (!headerProps.interactedWith) {
          await addInteraction({
            variables: {
              campaignId: params.campaignId ?? '',
              hitId: headerProps.hitItemId,
            },
          })
        }
        setIsLoading(false)
        setValue('')
        setErrorMessage(null)
      })
      .catch((e) => {
        if (e.response.status === 401) {
          setErrorMessage(
            'Please connect your socials before replying to this hit. To connect your socials, go to "Connect Social" section in our app.'
          )
        } else {
          setErrorMessage(
            e.response.data.message ??
              'Something went wrong please try again later.'
          )
        }
        setIsLoading(false)
        console.error(e)
      })
  }
  return (
    <div>
      <HitModalStyle.BootstrapDialog
        onClose={() => {
          closeModal()
          setValue('')
          setErrorMessage(null)
        }}
        aria-labelledby="customized-dialog-title"
        open={openModal}
      >
        <HitModalStyle.ModalContentStyle dividers>
          <HitModalStyle.ModalContentHeader>
            <HitModalStyle.CampaignName>
              {headerProps?.campaignName}
            </HitModalStyle.CampaignName>
            <IconButton
              aria-label="close"
              onClick={() => {
                closeModal()
                setValue('')
                setErrorMessage(null)
              }}
              sx={{
                position: 'absolute',
                right: 12,
                top: 12,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>

            <HitModalStyle.HitDetailWrapper>
              <HitModalStyle.HitUserDetail>
                <HitModalStyle.AvatarImage
                  src={headerProps?.avatar ?? DEFAULT_AVATAR_URL}
                  alt="AVATAR"
                />
                <div>
                  <HitModalStyle.Text>
                    Added by <b>{headerProps?.authorName}</b>, {displayDate}
                  </HitModalStyle.Text>
                  {headerProps.followersCount && (
                    <HitModalStyle.Text>
                      {headerProps.platform === 'Reddit'
                        ? 'Karma: '
                        : 'Followers: '}
                      <b>{formatLongNumber(headerProps.followersCount)}</b>
                    </HitModalStyle.Text>
                  )}
                </div>
              </HitModalStyle.HitUserDetail>
            </HitModalStyle.HitDetailWrapper>
            <HitModalStyle.KeywordsWrapper>
              <Keyword
                platform={headerProps?.platform}
                text={headerProps?.platform}
              />
            </HitModalStyle.KeywordsWrapper>
            <ClassificationTag tagName={headerProps.tag ?? ''} />
            <HitModalStyle.HitKeywordWrapper>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HitModalStyle.Text>Keywords:</HitModalStyle.Text>
                {headerProps?.keyword?.map((item, index) => (
                  <Keyword key={index} text={item} />
                ))}
              </Box>
            </HitModalStyle.HitKeywordWrapper>
          </HitModalStyle.ModalContentHeader>
          {returnEmbed()}
          <HitModalStyle.ModalContentBody>
            <HitModalStyle.TitleWrapper>
              <HitModalStyle.DescriptionIconStyle />
              <HitModalStyle.SectionTitle>
                Description
              </HitModalStyle.SectionTitle>
            </HitModalStyle.TitleWrapper>
            <HitModalStyle.Text
              dangerouslySetInnerHTML={{ __html: highlightKeyword(body) }}
            ></HitModalStyle.Text>
            {headerProps.platform !== 'Instagram' &&
            headerProps.platform !== 'TikTok' ? (
              <>
                <HitModalStyle.TitleWrapper>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      // justifyContent: 'flex-start',
                    }}
                  >
                    <HitModalStyle.ChatIconStyle />
                    <HitModalStyle.SectionTitle>
                      Respond
                    </HitModalStyle.SectionTitle>
                  </div>
                  <Button
                    variant="outlined"
                    color="success"
                    disabled={responseLoading || Boolean(data)}
                    onClick={() => {
                      getResponse({
                        variables: {
                          platform: headerProps.platform as Platform,
                          videoId: headerProps.hitItemId,
                          content: body,
                        },
                      }).catch((e) => console.log(e))
                    }}
                  >
                    Recommend response
                  </Button>
                </HitModalStyle.TitleWrapper>
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    paddingBottom: '10px',
                  }}
                >
                  {responseLoading && <CircularProgress />}
                  {responses && !responseLoading && (
                    <div
                      style={{
                        width: '100%',
                      }}
                    >
                      <List
                        component="nav"
                        sx={{
                          bgcolor: 'grey.900',
                          borderRadius: '4px',
                          boxShadow: '0px 2px 4px rgba(255, 255, 255, 0.1)',
                          color: 'grey.300',
                        }}
                      >
                        <ListItem
                          button
                          onClick={() => setValue(responses?.comment1 ?? value)}
                          sx={{
                            '&:hover': {
                              bgcolor: 'grey.800',
                            },
                          }}
                        >
                          <ListItemText
                            primary={responses?.comment1}
                            primaryTypographyProps={{ color: 'inherit' }}
                          />
                        </ListItem>
                        <Divider sx={{ bgcolor: 'grey.700' }} />
                        <ListItem
                          button
                          divider
                          onClick={() => {
                            setValue(responses?.comment2 ?? value)
                          }}
                          sx={{
                            '&:hover': {
                              bgcolor: 'grey.800',
                            },
                          }}
                        >
                          <ListItemText
                            primary={responses?.comment2}
                            primaryTypographyProps={{ color: 'inherit' }}
                          />
                        </ListItem>
                        <ListItem
                          button
                          onClick={() => setValue(responses?.comment3 ?? value)}
                          sx={{
                            '&:hover': {
                              bgcolor: 'grey.800',
                            },
                          }}
                        >
                          <ListItemText
                            primary={responses?.comment3}
                            primaryTypographyProps={{ color: 'inherit' }}
                          />
                        </ListItem>
                      </List>
                    </div>
                  )}
                  {!responses && data && (
                    <Alert severity="info">
                      We couldn&apos;t recommend any responses
                    </Alert>
                  )}
                  {error && (
                    <Alert severity="error">
                      Something went wrong, please try again!
                    </Alert>
                  )}
                </div>
                <RespondTextArea
                  setValue={setValue}
                  value={value}
                  onSubmit={handleRespond}
                  errorMessage={errorMessage}
                  loading={loading}
                />
              </>
            ) : (
              <div
                style={{
                  width: '100%',
                  marginTop: '10px',
                }}
              >
                <Button
                  variant="outlined"
                  color="success"
                  disabled={responseLoading || Boolean(data)}
                  onClick={() => {
                    getResponse({
                      variables: {
                        platform: headerProps.platform as Platform,
                        videoId: headerProps.hitItemId,
                        content: body,
                      },
                    }).catch((e) => console.log(e))
                  }}
                >
                  Recommend response
                </Button>
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    paddingBottom: '10px',
                  }}
                >
                  {responseLoading && <CircularProgress />}
                  {responses && !responseLoading && (
                    <div
                      style={{
                        width: '100%',
                      }}
                    >
                      <List
                        component="nav"
                        sx={{
                          bgcolor: 'grey.900',
                          borderRadius: '4px',
                          boxShadow: '0px 2px 4px rgba(255, 255, 255, 0.1)',
                          color: 'grey.300',
                        }}
                      >
                        <ListItem
                          button
                          onClick={() => setValue(responses?.comment1 ?? value)}
                          sx={{
                            '&:hover': {
                              bgcolor: 'grey.800',
                            },
                          }}
                        >
                          <ListItemText
                            primary={responses?.comment1}
                            primaryTypographyProps={{ color: 'inherit' }}
                          />
                        </ListItem>
                        <Divider sx={{ bgcolor: 'grey.700' }} />
                        <ListItem
                          button
                          divider
                          onClick={() => {
                            setValue(responses?.comment2 ?? value)
                          }}
                          sx={{
                            '&:hover': {
                              bgcolor: 'grey.800',
                            },
                          }}
                        >
                          <ListItemText
                            primary={responses?.comment2}
                            primaryTypographyProps={{ color: 'inherit' }}
                          />
                        </ListItem>
                        <ListItem
                          button
                          onClick={() => setValue(responses?.comment3 ?? value)}
                          sx={{
                            '&:hover': {
                              bgcolor: 'grey.800',
                            },
                          }}
                        >
                          <ListItemText
                            primary={responses?.comment3}
                            primaryTypographyProps={{ color: 'inherit' }}
                          />
                        </ListItem>
                      </List>
                      <Snackbar
                        open={open}
                        autoHideDuration={6000}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'center',
                        }}
                      >
                        <Alert
                          onClose={handleClose}
                          severity="success"
                          sx={{ width: '100%' }}
                        >
                          Copied to Clipboard.
                        </Alert>
                      </Snackbar>
                    </div>
                  )}
                  {!responses && data && (
                    <Alert severity="info">
                      We couldn&apos;t recommend any responses
                    </Alert>
                  )}
                  {error && (
                    <Alert severity="error">
                      Something went wrong, please try again!
                    </Alert>
                  )}
                </div>
              </div>
            )}
            <HitModalStyle.ModalContentFooter>
              <HitModalStyle.ReactionWrapper>
                <HitModalStyle.FavoriteIconStyle />
                <HitModalStyle.ReactionCounter>
                  {formatLongNumber(headerProps?.reactionCount)}
                </HitModalStyle.ReactionCounter>

                <HitModalStyle.ShareIconStyle />
                <HitModalStyle.ReactionCounter>
                  {formatLongNumber(headerProps?.shareCount)}
                </HitModalStyle.ReactionCounter>
              </HitModalStyle.ReactionWrapper>
            </HitModalStyle.ModalContentFooter>
          </HitModalStyle.ModalContentBody>
        </HitModalStyle.ModalContentStyle>
      </HitModalStyle.BootstrapDialog>
    </div>
  )
}
